import dayjs from "dayjs";
import type Fuse from "fuse.js";
import { produce } from "immer";
import { groupBy } from "ramda";

import {
    type FormattedValue,
    type FundData,
    type ListingData,
    type ShareClassData,
    type TopLevelDataObj,
    type TopLevelDataType,
} from "@hsl/core/fund-explorer/types";
import {
    indexedDataToSearchList,
    type SearchData,
} from "@hsl/fund-explorer/search";

import { type FilterConfig } from "../config/types";
import type {
    EntityIdData,
    Filter,
    FundExplorerFilters,
    FundExplorerSearch,
    FundExplorerState,
    RefineFnArgs,
} from "./types";

export function resultsGroupedByType(
    getter: (id: string) => TopLevelDataObj | undefined,
    ids: string[],
) {
    const funds = new Set<string>();
    const shareClasses = new Set<string>();
    const listings = new Set<string>();

    ids.map(getter).map(addObjectHierarchy);

    return {
        funds: Array.from(funds),
        shareClasses: Array.from(shareClasses),
        listings: Array.from(listings),
    };

    function addObjectHierarchy(
        obj: TopLevelDataObj | undefined,
    ): TopLevelDataObj | undefined {
        if (!obj) {
            return;
        }
        switch (obj.type) {
            case "fund":
                if (funds.has(obj.localId)) {
                    return;
                }
                funds.add(obj.localId);
                (obj as FundData).share_classes
                    .map(generateLocalId("shareclass"))
                    .map(getter)
                    .forEach(addObjectHierarchy);
                break;
            case "shareclass":
                if (shareClasses.has(obj.localId)) {
                    return;
                }
                shareClasses.add(obj.localId);
                (obj as ShareClassData).listings
                    ?.map(generateLocalId("listing"))
                    .map(getter)
                    .forEach(addObjectHierarchy);
                break;
            case "listing":
                if (listings.has(obj.localId)) {
                    return;
                }
                listings.add(obj.localId);
                break;
            default:
                return obj;
        }
        return obj.parentId ? addObjectHierarchy(getter(obj.parentId)) : obj;
    }
}

export function applyStandardFilters(
    getter: (id: string) => TopLevelDataObj | undefined,
    availableObjIds: FundExplorerSearch["results"],
    filters: Filter[],
): EntityIdData {
    const fundFilters = filters.filter((f) => f.level === "fund");
    const shareClassFilters = filters.filter((f) => f.level === "shareclass");
    const listingFilters = filters.filter((f) => f.level === "listing");
    let matchingFunds: string[] = [];
    let matchingShareClasses: string[] = [];
    let matchingListings: string[] = [];

    if (availableObjIds && availableObjIds.funds.length) {
        const filterFn = (
            { prop, value }: Filter,
            obj: TopLevelDataObj | undefined,
        ) => {
            return obj ? matchFilterValue(obj.data[prop], value) : false;
        };
        matchingFunds = fundFilters.length
            ? filterObjectTypes(
                  fundFilters,
                  availableObjIds.funds,
                  getter,
                  filterFn,
              )
            : availableObjIds.funds;

        const matchingFundsShareClasses = matchingFunds.length
            ? matchingFunds.map(getter).flatMap((f) =>
                  (f as FundData).share_classes
                      .map(generateLocalId("shareclass"))
                      .map(getter)
                      .filter((sc) => Boolean(sc))
                      .map((sc) => sc!.localId),
              )
            : [];
        matchingShareClasses = shareClassFilters.length
            ? filterObjectTypes(
                  shareClassFilters,
                  matchingFundsShareClasses,
                  getter,
                  filterFn,
              )
            : matchingFundsShareClasses;

        const matchingShareClassesListings = matchingShareClasses.length
            ? matchingShareClasses
                  .map(getter)
                  .map((sc) => (sc as ShareClassData).listings)
                  .filter((l) => Boolean(l) && l.length)
                  .flat()
                  .map(generateLocalId("listing"))
                  .map(getter)
                  .map((l) => (l as ListingData).localId)
            : [];
        // ToDo: listing/iteration filtering.
        matchingListings = listingFilters.length
            ? filterObjectTypes(
                  listingFilters,
                  matchingShareClassesListings,
                  getter,
                  filterFn,
              )
            : matchingShareClassesListings;
    }

    return {
        funds: matchingFunds,
        shareClasses: matchingShareClasses,
        listings: matchingListings,
    };
}

function matchFilterValue(
    indexedVal: FormattedValue | undefined,
    filterVal: Filter["value"],
): boolean {
    if (Array.isArray(filterVal)) {
        return filterVal.some((fv) => indexedVal === fv);
    }
    return filterVal === indexedVal;
}

function filterObjectTypes(
    filters: Filter[],
    availableIds: string[],
    getter: (id: string) => TopLevelDataObj | undefined,
    filterFn: (f: Filter, x: TopLevelDataObj | undefined) => boolean,
): string[] {
    if (!filters.length) {
        return availableIds;
    }
    return filters.reduce((available, filter) => {
        return available.filter((id) => {
            const obj = getter(id);
            if (!obj) {
                return false;
            }
            return filterFn(filter, obj);
        });
    }, availableIds);
}

export function applyGroupBy(
    getter: (id: string) => TopLevelDataObj | undefined,
    availableObjIds: EntityIdData["funds"],
    prop: string,
) {
    if (!Array.isArray(availableObjIds) || !availableObjIds.length) {
        return {};
    }
    return groupBy((id) => getter(id)?.data[prop] as string, availableObjIds);
}

export function searchHelper(
    state: FundExplorerState,
    searchTerm: string | undefined,
    reset: boolean,
) {
    const searchOn = state.config.search.searchOn;
    const term = reset
        ? ""
        : typeof searchTerm === "string"
          ? searchTerm
          : state.search.term;
    const isActiveSearch = term.length >= state.config.search.minChars;
    const query = {
        $or: searchOn.map((prop) => ({
            $and: term.split(" ").map((t) => ({
                $or: [`'${t}`, t].map((_t) => ({ [prop]: _t })),
            })),
        })),
    };
    const list = indexedDataToSearchList(state.indexedData!);
    const simpleMatch = (value: string) =>
        value.toLowerCase().includes(term.toLowerCase());
    const simpleIndex = (value: string) =>
        value.toLowerCase().indexOf(term.toLowerCase());
    const toFuseResultMatch = (item: SearchData) => ({
        id: item.id,
        matches: searchOn
            .filter((prop) => simpleMatch(`${item[prop]}`))
            .map((prop) => ({
                indices: [
                    [
                        simpleIndex(`${item[prop]}`),
                        simpleIndex(`${item[prop]}`) + term.length - 1,
                    ],
                ],
                value: item[prop],
                key: prop,
            })) as readonly Fuse.FuseResultMatch[],
    });
    const simpleExactResults = isActiveSearch
        ? list
              .filter((item) =>
                  searchOn.some(
                      (prop) =>
                          `${item[prop]}`.toLowerCase() === term.toLowerCase(),
                  ),
              )
              .map(toFuseResultMatch)
        : [];
    const simpleResults =
        isActiveSearch && simpleExactResults.length === 0
            ? list
                  .filter((item) =>
                      searchOn.some((prop) => simpleMatch(`${item[prop]}`)),
                  )
                  .map(toFuseResultMatch)
            : simpleExactResults;
    const rawResults =
        isActiveSearch && simpleResults.length === 0
            ? state.search.searchFn(query)
            : simpleResults;

    const searchResults = isActiveSearch
        ? resultsGroupedByType(
              state.getDataById,
              rawResults.map((m) => m.id),
          )
        : state.objectIds;

    return {
        term,
        isActiveSearch,
        searchResults: {
            ...searchResults,
            funds: sortFundsByDescriptor(
                state.getDataById,
                searchResults.funds,
            ),
        },
        rawResults,
    };
}

export function filterHelper(
    state: FundExplorerState,
    args: Partial<FundExplorerFilters>,
    searchResults: EntityIdData,
    reset: boolean,
) {
    const primary = reset ? [] : args.primary ?? state.filters.primary;
    const secondary = reset ? [] : args.secondary ?? state.filters.secondary;
    const standardFilters = [...primary, ...secondary];
    const hasFundFilters = standardFilters.some((f) => f.level === "fund");
    const hasShareClassFilters = standardFilters.some(
        (f) => f.level === "shareclass",
    );
    const hasListingFilters = standardFilters.some(
        (f) => f.level === "listing",
    );
    const advanced = reset ? [] : args.advanced ?? state.filters.advanced;
    const filteredData = standardFilters.length
        ? applyStandardFilters(
              state.getDataById,
              searchResults,
              standardFilters,
          )
        : searchResults;

    return {
        primary,
        secondary,
        advanced,
        hasFundFilters,
        hasShareClassFilters,
        hasListingFilters,
        filteredData: {
            ...filteredData,
            funds: sortFundsByDescriptor(state.getDataById, filteredData.funds),
        },
    };
}

export function sortFundsByDescriptor(
    getter: FundExplorerState["getDataById"],
    list: string[],
) {
    const isDataGuard = (
        d: TopLevelDataObj | undefined,
    ): d is TopLevelDataObj => Boolean(d);
    return list
        .map(getter)
        .filter(isDataGuard)
        .filter((d) => d.type === "fund")
        .sort((a, b) => {
            return (a?.data?.descriptor as string)?.localeCompare?.(
                b?.data?.descriptor as string,
            );
        })
        .map((d) => d.localId)
        .filter((id) => id !== undefined);
}

export const generateLocalId = (type: TopLevelDataType) => (id: number) =>
    `${type}-${id}`;

export const searchParamsToAppliedFilters = (configs: FilterConfig[]) => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.size > 0) {
        const applied = Array.from(sp.keys())
            .map((prop) => {
                const config = configs.find(
                    (c) =>
                        c.prop === prop ||
                        c.options?.some((o) => o.prop === prop),
                );
                const value = sp.get(prop)?.split(",") as string[];
                if (config) {
                    const { level, type, options, prop } = config;
                    const option = options?.find((o) => o.prop === prop);
                    const filter = {
                        level,
                        prop: config.prop ? prop : option?.prop ?? "",
                        type,
                        value,
                    };
                    return filter;
                }
                return false;
            })
            .reduce(
                (applied, f) => {
                    if (f) {
                        return produce(applied, (draft) => {
                            draft[f.type] = draft[f.type]
                                ? [...draft[f.type], f]
                                : [f];
                        });
                    }
                    return applied;
                },
                {} as FundExplorerState["filters"],
            );
        return sp.get("search")
            ? { ...applied, term: sp.get("search") }
            : applied;
    }
    return {};
};

export const appliedFiltersToSearchParams = (args: RefineFnArgs) => {
    const currentSP = new URLSearchParams(window.location.search);
    const defaultParams = ["branch", "version"]
        .map((p) => [p, currentSP.get(p)])
        .filter(([_, val]) => Boolean(val));
    const search =
        typeof args.term === "string" && args.term.length > 0
            ? [["search", args.term]]
            : [];
    const { primary = [], secondary = [], advanced = [] } = args;
    const filters = [...primary, ...secondary, ...advanced]
        .filter((f) => !f.associatedFilters)
        .map((f) => [
            f.prop,
            Array.isArray(f.value) ? f.value.join(",") : f.value,
        ]);
    const url = [...defaultParams, ...filters, ...search].reduce(
        (url, [key, val], i) => {
            if (i === 0) {
                url.search = "";
            }
            url.searchParams.set(key!, val!);
            return url;
        },
        new URL(window.location.href),
    );
    window.history.replaceState(args, "", url);
};

export const downloadFilename = (
    name: string,
    translation: Record<string, string>,
) => {
    const regex = /(\{[\s]*date[\s]*\})/gi;
    return name
        .split(regex)
        .filter((s) => s.trim().length > 0)
        .reduce((fname, substr) => {
            const space = fname.length > 0 ? " " : "";
            return `${fname}${space}${
                regex.test(substr)
                    ? dayjs().format("YYYY-MM-DD")
                    : translation[substr.trim()] ?? substr
            }`;
        }, "");
};

export function sortHelper(state: FundExplorerState, funds: string[]) {
    const { sortBy, sortDirection } = state;
    const sorted = funds
        .map(state.getFundById)
        .sort((a, b) => {
            if (sortBy === null) return 0;
            if (a?.type !== "pmc" || b?.type !== "pmc") return 0;
            const aVal = a.data.data[sortBy];
            const bVal = b.data.data[sortBy];
            if (!aVal || !bVal) return 0;
            if (typeof aVal === "number" && typeof bVal === "number") {
                return aVal - bVal;
            } else if (typeof aVal === "string" && typeof bVal === "string") {
                if (aVal === bVal) return 0;
                if (aVal === "-") return 1;
                if (bVal === "-") return -1;
                return aVal.localeCompare(bVal);
            } else {
                return 0;
            }
        })
        .map((d) => d?.data.localId)
        .filter((id) => id !== undefined) as string[];

    if (sortDirection === "asc") sorted.reverse();

    return sorted;
}
